import React, { useState, useEffect, useRef } from 'react';
import {
  Image, Plus, Edit, Trash2, Eye, EyeOff, Calendar, Link2, Monitor, Smartphone,
  RefreshCw, ArrowLeft, CheckCircle, XCircle, Upload, ChevronDown, ChevronUp,
  Target, MousePointer, TrendingUp, BarChart3, Clock, Bell, DollarSign, Mail,
  User, Building2, AlertCircle, Check, X
} from 'lucide-react';
import { bannerAds, storage, bannerAdRequests } from '../lib/supabase';
import supabase from '../lib/supabase';

// ==================== PAGES LIST ====================
const AVAILABLE_PAGES = [
  { id: 'home', label: 'Home Page' },
  { id: 'schools', label: 'School Directory' },
  { id: 'exams', label: 'Exams Hub' },
  { id: 'rankings', label: 'School Rankings' },
  { id: 'funding', label: 'Scholarships & Funding' },
  { id: 'services', label: 'Education Services' },
  { id: 'getlisted', label: 'Get Listed Page' },
  { id: 'career-path', label: 'Career Planning' },
  { id: 'compare-schools', label: 'Compare Schools' },
  { id: 'askbot', label: 'Education Assistant' },
  { id: 'resources', label: 'Resources' }
];

// ==================== PLACEMENT OPTIONS ====================
const PLACEMENT_OPTIONS = [
  { id: 'top', label: 'Top Banner (728x90)', description: 'Shows at the top of the page, below header' },
  { id: 'bottom', label: 'Bottom Banner (728x90)', description: 'Shows at the bottom of the page' },
  { id: 'inline1', label: 'Inline Position 1 (468x60)', description: 'First inline slot - between top content sections' },
  { id: 'inline2', label: 'Inline Position 2 (468x60)', description: 'Second inline slot - mid-page content break' },
  { id: 'inline3', label: 'Inline Position 3 (468x60)', description: 'Third inline slot - before footer sections' },
  { id: 'mid-content', label: 'Mid-Content (468x60)', description: 'Generic mid-content placement' },
  { id: 'sidebar', label: 'Sidebar (300x250)', description: 'Side column placement (if applicable)' }
];

// Format currency
const formatCurrency = (amount) => `J$${(amount || 0).toLocaleString()}`;
const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'N/A';

// ==================== ADMIN BANNER ADS PAGE ====================
export const AdminBannerAdsPage = ({ setPage }) => {
  const [activeTab, setActiveTab] = useState('ads'); // 'ads' | 'requests'
  const [ads, setAds] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewDesktop, setPreviewDesktop] = useState(null);
  const [previewMobile, setPreviewMobile] = useState(null);
  const fileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [adsRes, reqRes] = await Promise.all([
      bannerAds.getAll(),
      supabase.from('banner_ad_requests').select('*').order('created_at', { ascending: false })
    ]);
    setAds(adsRes.data || []);
    setRequests(reqRes.data || []);
    setLoading(false);
  };

  const loadAds = loadData; // Alias for compatibility

  // Get pending requests count for badge
  const pendingCount = requests.filter(r => ['pending', 'payment_submitted'].includes(r.status)).length;

  const handleFileUpload = async (e, isMobile = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (event) => {
      if (isMobile) {
        setPreviewMobile(event.target.result);
      } else {
        setPreviewDesktop(event.target.result);
      }
    };
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `ads/${Date.now()}_${isMobile ? 'mobile' : 'desktop'}.${ext}`;
      const { error } = await storage.uploadImage('banner-ads', path, file);

      if (!error) {
        const url = storage.getPublicUrl('banner-ads', path);
        setFormData({
          ...formData,
          [isMobile ? 'image_url_mobile' : 'image_url']: url
        });
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await bannerAds.create(formData);
      } else {
        await bannerAds.update(editing, formData);
      }
      loadAds();
      setEditing(null);
      setFormData({});
      setPreviewDesktop(null);
      setPreviewMobile(null);
    } catch (error) {
      console.error('Error saving ad:', error);
      alert('Error saving ad: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this advertisement?')) return;
    try {
      await bannerAds.delete(id);
      loadAds();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleToggleActive = async (ad) => {
    try {
      await bannerAds.update(ad.id, { is_active: !ad.is_active });
      loadAds();
    } catch (error) {
      console.error('Error toggling:', error);
    }
  };

  const startNew = () => {
    setEditing('new');
    setFormData({
      name: '',
      placement: 'top',
      pages: ['home'],
      image_url: '',
      image_url_mobile: '',
      link_url: '',
      alt_text: '',
      advertiser_name: '',
      advertiser_email: '',
      start_date: '',
      end_date: '',
      priority: 0,
      is_active: true
    });
    setPreviewDesktop(null);
    setPreviewMobile(null);
  };

  const startEdit = (ad) => {
    setEditing(ad.id);
    setFormData(ad);
    setPreviewDesktop(ad.image_url);
    setPreviewMobile(ad.image_url_mobile);
  };

  // Status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      awaiting_payment: 'bg-blue-100 text-blue-700',
      payment_submitted: 'bg-purple-100 text-purple-700',
      payment_verified: 'bg-brand-100 text-brand-700',
      design_pending: 'bg-orange-100 text-orange-700',
      design_review: 'bg-indigo-100 text-indigo-700',
      active: 'bg-green-100 text-green-700',
      paused: 'bg-gray-100 text-gray-600',
      completed: 'bg-slate-100 text-slate-600',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    const labels = {
      pending: 'Pending Review',
      awaiting_payment: 'Awaiting Payment',
      payment_submitted: 'Payment Submitted',
      payment_verified: 'Payment Verified',
      design_pending: 'Awaiting Artwork',
      design_review: 'Artwork Review',
      active: 'Active',
      paused: 'Paused',
      completed: 'Completed',
      rejected: 'Rejected',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Handle request actions
  const handleApproveRequest = async (request) => {
    try {
      await supabase.from('banner_ad_requests').update({
        status: 'awaiting_payment',
        updated_at: new Date().toISOString()
      }).eq('id', request.id);
      loadData();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error approving request');
    }
  };

  const handleVerifyPayment = async (request) => {
    try {
      // Check if artwork already submitted
      const newStatus = request.ad_image_url ? 'design_review' : 'design_pending';
      await supabase.from('banner_ad_requests').update({
        status: newStatus,
        payment_verified_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }).eq('id', request.id);
      loadData();
    } catch (error) {
      console.error('Error verifying payment:', error);
      alert('Error verifying payment');
    }
  };

  const handleApproveArtwork = async (request) => {
    try {
      await supabase.from('banner_ad_requests').update({
        status: 'payment_verified',
        updated_at: new Date().toISOString()
      }).eq('id', request.id);
      loadData();
    } catch (error) {
      console.error('Error approving artwork:', error);
      alert('Error approving artwork');
    }
  };

  const handleRejectRequest = async (request) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;

    try {
      await supabase.from('banner_ad_requests').update({
        status: 'rejected',
        rejection_reason: reason,
        updated_at: new Date().toISOString()
      }).eq('id', request.id);
      loadData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rejecting request');
    }
  };

  const handleActivateRequest = async (request) => {
    try {
      // Calculate end date based on duration
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + request.duration_months);

      // Create the actual banner ad
      const { data: newAd, error: adError } = await supabase.from('banner_ads').insert({
        name: `${request.business_name} - ${request.placement} Banner`,
        placement: request.placement,
        pages: request.target_pages || ['home'],
        image_url: request.ad_image_url,
        image_url_mobile: request.ad_image_url_mobile,
        link_url: request.ad_link_url,
        alt_text: request.ad_alt_text || request.business_name,
        advertiser_name: request.business_name,
        advertiser_email: request.contact_email,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        is_active: true,
        priority: 1
      }).select().single();

      if (adError) throw adError;

      // Update the request with banner ad reference
      await supabase.from('banner_ad_requests').update({
        status: 'active',
        banner_ad_id: newAd.id,
        actual_start_date: startDate.toISOString().split('T')[0],
        actual_end_date: endDate.toISOString().split('T')[0],
        updated_at: new Date().toISOString()
      }).eq('id', request.id);

      loadData();
      alert('Banner ad activated successfully!');
    } catch (error) {
      console.error('Error activating:', error);
      alert('Error activating banner ad: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Image className="w-8 h-8" />Banner Ad Management
            </h2>
            <p className="text-amber-100 mt-1">Create and manage banner advertisements</p>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === 'ads' && (
              <button onClick={startNew} className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
                <Plus className="w-5 h-5" />New Ad
              </button>
            )}
            <button onClick={loadAds} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => setActiveTab('ads')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'ads'
                ? 'bg-white text-amber-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            Active Ads ({ads.filter(a => a.is_active).length})
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'requests'
                ? 'bg-white text-amber-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            Ad Requests
            {pendingCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            <div className="bg-amber-500 text-white p-4 rounded-t-2xl">
              <h3 className="font-bold">{editing === 'new' ? 'Create New Ad' : 'Edit Ad'}</h3>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad Name *</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                    placeholder="e.g., ABC Insurance - Jan 2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Placement *</label>
                  <select
                    value={formData.placement || 'top'}
                    onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  >
                    {PLACEMENT_OPTIONS.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                  {formData.placement && (
                    <p className="text-xs text-gray-500 mt-1">
                      {PLACEMENT_OPTIONS.find(p => p.id === formData.placement)?.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Pages Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Show on Pages *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {AVAILABLE_PAGES.map(page => (
                    <label key={page.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={(formData.pages || []).includes(page.id)}
                        onChange={(e) => {
                          const pages = formData.pages || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, pages: [...pages, page.id] });
                          } else {
                            setFormData({ ...formData, pages: pages.filter(p => p !== page.id) });
                          }
                        }}
                        className="rounded text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-sm">{page.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Image Uploads */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Monitor className="w-4 h-4 inline mr-1" />Desktop Image (728x90) *
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-amber-500 transition-colors"
                  >
                    {previewDesktop ? (
                      <img src={previewDesktop} alt="Desktop preview" className="max-h-24 max-w-full mx-auto object-contain" />
                    ) : (
                      <div className="text-gray-400">
                        <Upload className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">Click to upload</p>
                        <p className="text-xs">Recommended: 728x90 PNG/JPG</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, false)}
                    className="hidden"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Smartphone className="w-4 h-4 inline mr-1" />Mobile Image (320x50)
                  </label>
                  <div
                    onClick={() => mobileFileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-amber-500 transition-colors"
                  >
                    {previewMobile ? (
                      <img src={previewMobile} alt="Mobile preview" className="max-h-24 max-w-full mx-auto object-contain" />
                    ) : (
                      <div className="text-gray-400">
                        <Upload className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">Click to upload (optional)</p>
                        <p className="text-xs">Recommended: 320x50 PNG/JPG</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={mobileFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, true)}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Link & Alt Text */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Link2 className="w-4 h-4 inline mr-1" />Target URL *
                  </label>
                  <input
                    type="url"
                    value={formData.link_url || ''}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
                  <input
                    type="text"
                    value={formData.alt_text || ''}
                    onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                    placeholder="Description for accessibility"
                  />
                </div>
              </div>

              {/* Advertiser Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser Name</label>
                  <input
                    type="text"
                    value={formData.advertiser_name || ''}
                    onChange={(e) => setFormData({ ...formData, advertiser_name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser Email</label>
                  <input
                    type="email"
                    value={formData.advertiser_email || ''}
                    onChange={(e) => setFormData({ ...formData, advertiser_email: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                    placeholder="contact@example.com"
                  />
                </div>
              </div>

              {/* Schedule & Priority */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    value={formData.priority || 0}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                    placeholder="0 = lowest"
                    min="0"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="font-medium text-gray-700">Active</label>
                  <p className="text-sm text-gray-500">Show this ad on the site</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${formData.is_active ? 'bg-brand-500' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${formData.is_active ? 'left-8' : 'left-1'}`} />
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={handleSave}
                  disabled={uploading || !formData.name || !formData.image_url || !formData.link_url}
                  className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Save Ad'}
                </button>
                <button
                  onClick={() => { setEditing(null); setFormData({}); setPreviewDesktop(null); setPreviewMobile(null); }}
                  className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content based on active tab */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : activeTab === 'ads' ? (
        /* ==================== ADS TAB ==================== */
        ads.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl p-12 text-center">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-500">No Ads Yet</h3>
            <p className="text-gray-400 mt-1">Create your first banner ad to get started</p>
            <button onClick={startNew} className="mt-4 px-6 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600">
              <Plus className="w-5 h-5 inline mr-2" />Create Ad
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {ads.map((ad) => (
              <div key={ad.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="flex items-stretch">
                  {/* Preview */}
                  <div className="w-48 bg-gray-100 flex items-center justify-center p-4">
                    {ad.image_url ? (
                      <img src={ad.image_url} alt={ad.alt_text || ad.name} className="max-w-full max-h-20 object-contain" />
                    ) : (
                      <Image className="w-12 h-12 text-gray-300" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-bold text-lg">{ad.name}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${ad.is_active ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-600'}`}>
                            {ad.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium capitalize">{ad.placement}</span>
                        </div>
                        {ad.advertiser_name && (
                          <p className="text-gray-500 text-sm mt-1">Advertiser: {ad.advertiser_name}</p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(ad.pages || []).map((p, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              {typeof p === 'object' ? (p.label || p.value || '') : p}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-900">{(ad.impressions || 0).toLocaleString()}</p>
                          <p className="text-gray-500">Impressions</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-amber-600">{(ad.clicks || 0).toLocaleString()}</p>
                          <p className="text-gray-500">Clicks</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{((ad.ctr || 0) * 100).toFixed(2)}%</p>
                          <p className="text-gray-500">CTR</p>
                        </div>
                      </div>
                    </div>

                    {/* Schedule */}
                    {(ad.start_date || ad.end_date) && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="w-4 h-4" />
                        {ad.start_date && <span>From {ad.start_date}</span>}
                        {ad.end_date && <span>to {ad.end_date}</span>}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 flex gap-2">
                      <button onClick={() => startEdit(ad)} className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium">
                        <Edit className="w-4 h-4 inline mr-1" />Edit
                      </button>
                      <button onClick={() => handleToggleActive(ad)} className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium">
                        {ad.is_active ? <EyeOff className="w-4 h-4 inline mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
                        {ad.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => handleDelete(ad.id)} className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">
                        <Trash2 className="w-4 h-4 inline mr-1" />Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* ==================== REQUESTS TAB ==================== */
        <div className="space-y-4">
          {/* Request Status Summary */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {/* Total Ads Stat */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
              <Image className="w-5 h-5 mx-auto text-amber-500" />
              <p className="text-2xl font-bold text-gray-800">{ads.length}</p>
              <p className="text-xs text-gray-600">Total Ads</p>
            </div>
            {[
              { status: 'pending', label: 'Pending', icon: Clock, color: 'yellow' },
              { status: 'awaiting_payment', label: 'Awaiting Payment', icon: DollarSign, color: 'blue' },
              { status: 'payment_submitted', label: 'Payment Review', icon: CheckCircle, color: 'purple' },
              { status: 'design_pending', label: 'Needs Artwork', icon: Upload, color: 'orange' },
              { status: 'active', label: 'Active Requests', icon: Eye, color: 'green' }
            ].map(({ status, label, icon: Icon, color }) => {
              const count = requests.filter(r => r.status === status).length;
              return (
                <div key={status} className={`bg-${color}-50 border border-${color}-200 rounded-xl p-3 text-center`}>
                  <Icon className={`w-5 h-5 mx-auto text-${color}-500`} />
                  <p className="text-2xl font-bold text-gray-800">{count}</p>
                  <p className="text-xs text-gray-600">{label}</p>
                </div>
              );
            })}
          </div>

          {/* Revenue Summary */}
          <div className="bg-gradient-to-r from-brand-50 to-brand-50 border border-brand-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brand-600 font-medium">Banner Ad Revenue</p>
                <p className="text-2xl font-black text-brand-700">
                  {formatCurrency(requests.filter(r => r.status === 'active').reduce((sum, r) => sum + (r.final_price || 0), 0))}
                </p>
                <p className="text-xs text-gray-500">from {requests.filter(r => r.status === 'active').length} active campaigns</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-600 font-medium">Pending Revenue</p>
                <p className="text-2xl font-black text-blue-700">
                  {formatCurrency(requests.filter(r => ['pending', 'awaiting_payment', 'payment_submitted', 'design_pending', 'design_review', 'payment_verified'].includes(r.status)).reduce((sum, r) => sum + (r.final_price || 0), 0))}
                </p>
                <p className="text-xs text-gray-500">from {requests.filter(r => !['active', 'completed', 'rejected', 'cancelled'].includes(r.status)).length} pending requests</p>
              </div>
            </div>
          </div>

          {/* Requests List */}
          {requests.length === 0 ? (
            <div className="bg-gray-50 rounded-2xl p-12 text-center">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-500">No Banner Ad Requests</h3>
              <p className="text-gray-400 mt-1">Requests from service providers will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div key={request.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="p-5">
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-bold text-lg">{request.business_name}</h3>
                          {getStatusBadge(request.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {request.contact_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {request.contact_email}
                          </span>
                          {request.contact_phone && (
                            <span>{request.contact_phone}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-brand-600">{formatCurrency(request.final_price)}</p>
                        <p className="text-xs text-gray-500">
                          {request.discount_applied > 0 && (
                            <span className="line-through mr-1">{formatCurrency(request.price)}</span>
                          )}
                          {request.duration_months} months
                        </p>
                      </div>
                    </div>

                    {/* Package Details */}
                    <div className="grid md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl mb-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Package</p>
                        <p className="font-medium">{request.package_name}</p>
                        <p className="text-sm text-gray-500 capitalize">{request.placement} placement</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Target Pages</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(request.target_pages || ['home']).map((p, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-white border rounded text-xs">
                              {typeof p === 'object' ? (p.label || p.value || '') : p}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Provider Type</p>
                        <p className="font-medium capitalize">{request.service_provider_type}</p>
                        <p className="text-xs text-gray-400">ID: {request.service_provider_id?.slice(0, 8)}...</p>
                      </div>
                    </div>

                    {/* Artwork Preview (if uploaded) */}
                    {request.ad_image_url && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                        <p className="text-xs text-blue-600 font-medium mb-2">SUBMITTED ARTWORK</p>
                        <div className="flex gap-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Desktop</p>
                            <img src={request.ad_image_url} alt="Desktop banner" className="max-h-20 border rounded" />
                          </div>
                          {request.ad_image_url_mobile && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Mobile</p>
                              <img src={request.ad_image_url_mobile} alt="Mobile banner" className="max-h-20 border rounded" />
                            </div>
                          )}
                        </div>
                        {request.ad_link_url && (
                          <p className="text-xs text-gray-500 mt-2">
                            Link: <a href={request.ad_link_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{request.ad_link_url}</a>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Payment Info (if submitted) */}
                    {request.payment_proof_url && (
                      <div className="mb-4 p-4 bg-purple-50 rounded-xl">
                        <p className="text-xs text-purple-600 font-medium mb-2">PAYMENT SUBMITTED</p>
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-sm">Method: <span className="font-medium capitalize">{request.payment_method}</span></p>
                            {request.payment_reference && (
                              <p className="text-sm">Reference: <span className="font-medium">{request.payment_reference}</span></p>
                            )}
                          </div>
                          {request.payment_proof_url && (
                            <a
                              href={request.payment_proof_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                            >
                              View Proof
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Notes */}
                    {request.admin_notes && (
                      <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-sm">
                        <p className="font-medium text-yellow-800">Admin Notes:</p>
                        <p className="text-yellow-700">{request.admin_notes}</p>
                      </div>
                    )}

                    {/* Rejection Reason */}
                    {request.rejection_reason && (
                      <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 text-sm">
                        <p className="font-medium text-red-800">Rejection Reason:</p>
                        <p className="text-red-700">{request.rejection_reason}</p>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                      <span>Submitted: {formatDate(request.created_at)}</span>
                      {request.payment_verified_at && (
                        <span>Payment verified: {formatDate(request.payment_verified_at)}</span>
                      )}
                      {request.actual_start_date && (
                        <span>Active: {request.actual_start_date} to {request.actual_end_date}</span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 pt-4 border-t">
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApproveRequest(request)}
                            className="flex items-center gap-1 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600"
                          >
                            <Check className="w-4 h-4" />Approve & Request Payment
                          </button>
                          <button
                            onClick={() => handleRejectRequest(request)}
                            className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />Reject
                          </button>
                        </>
                      )}

                      {request.status === 'payment_submitted' && (
                        <>
                          <button
                            onClick={() => handleVerifyPayment(request)}
                            className="flex items-center gap-1 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600"
                          >
                            <CheckCircle className="w-4 h-4" />Verify Payment
                          </button>
                          <button
                            onClick={() => handleRejectRequest(request)}
                            className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />Reject Payment
                          </button>
                        </>
                      )}

                      {request.status === 'design_review' && (
                        <>
                          <button
                            onClick={() => handleApproveArtwork(request)}
                            className="flex items-center gap-1 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600"
                          >
                            <CheckCircle className="w-4 h-4" />Approve Artwork
                          </button>
                          <button
                            onClick={() => handleRejectRequest(request)}
                            className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />Request Changes
                          </button>
                        </>
                      )}

                      {request.status === 'payment_verified' && request.ad_image_url && (
                        <button
                          onClick={() => handleActivateRequest(request)}
                          className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                        >
                          <Eye className="w-4 h-4" />Activate Banner Ad
                        </button>
                      )}

                      {request.status === 'design_pending' && (
                        <p className="text-sm text-orange-600 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Waiting for customer to upload banner artwork
                        </p>
                      )}

                      {request.status === 'awaiting_payment' && (
                        <p className="text-sm text-blue-600 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Waiting for customer payment
                        </p>
                      )}

                      {request.status === 'active' && request.banner_ad_id && (
                        <button
                          onClick={() => {
                            const ad = ads.find(a => a.id === request.banner_ad_id);
                            if (ad) startEdit(ad);
                            setActiveTab('ads');
                          }}
                          className="flex items-center gap-1 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600"
                        >
                          <Edit className="w-4 h-4" />View/Edit Active Ad
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

export default AdminBannerAdsPage;
